import { useState, useContext, useMemo, useEffect } from "react";
import { HMIContext } from "../../../../contexts/HMIContext/HMIContext";
import { OverallDowntimeSummaryPieChart } from "../../../../components/OverallDowntimeSummaryPieChart/OverallDowntimeSummaryPieChart.view";
import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useStyles } from "./OverallSummary.styles";
import { OverallDowntimeSummaryPieChartLegends } from "../../../../components/OverallDowntimeSummaryPieChartLegends/OverallDowntimeSummaryPieChartLegends.view";
import { truncateLongText } from "../../../../utilities/utilities";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { HMIUptimeDowntimeBar } from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBar.view";
import { OverallDowntimeSummaryPieChartCenteredMetric } from "../../../../components/OverallDowntimeSummaryPieChartCenteredMetric/OverallDowntimeSummaryPieChartCenteredMetric.view";
import { HMICachedDataContext } from "../../../../contexts/HMICachedDataContext/HMICachedDataContext";
import { NoStatsData } from "../NoStatsData/NoStatsData.view";
import { GraphsNotEnoughDataInMinutes } from "components/GraphsNotEnoughDataInMinutes/GraphsNotEnoughDataInMinutes.view";
import { IDataItem, IGraphProps, IPieChartData } from "./ShiftInterfaces";
import { DowntimeDetailsHeader } from "pages/Corporate/Locations/MillDashboards/LiveDowntimeReport/DowntimeReport/Components/DowntimeDetailsHeader/DowntimeDetailsHeader.view";
import { DefaultTranslationFieldsInTable } from "constants/constants";

export const OverallSummary = ({
    downtimeEvents,
    showTitle,
    showDowntimeDetailsHeader,
    downtimeSelected,
    countryCode,
}: IGraphProps) => {
    const { displayUnit } = useContext(HMIContext);
    const [highlight, setHighlight] = useState({ name: "", value: 0 });
    const { t, i18n } = useTranslation();
    const { store } = useContext(HMICachedDataContext);
    const classes = useStyles();
    const [noInitialDataInMinutes, setNoInitialDataInMinutes] = useState<boolean>(false);

    const pieChartData = useMemo(() => {
        const groupedData: IPieChartData[] = [];

        downtimeEvents?.forEach((obj) => {
            const defaultFailureTypeTranslatedName = translateDefaultEntity(
                i18n.language,
                obj.failure_type,
                "name",
                DefaultTranslationFieldsInTable.FAILURE_TYPE,
            );

            const existingItem = groupedData.find((item) => item[defaultFailureTypeTranslatedName]);

            if (!existingItem) {
                const newItem = {
                    [defaultFailureTypeTranslatedName]: {
                        id: truncateLongText(defaultFailureTypeTranslatedName, 25),
                        name: truncateLongText(defaultFailureTypeTranslatedName, 25),
                        value: displayUnit === "minutes" ? obj.validTime : 1,
                        color: obj.failure_type.color_code,
                    },
                };

                groupedData.push(newItem);
            } else {
                const existingFailureType = existingItem[defaultFailureTypeTranslatedName];

                existingFailureType.value += displayUnit === "minutes" ? obj.validTime : 1;
            }
        });

        displayUnit === "minutes" &&
            groupedData.forEach(
                (item) =>
                    (Object.values(item)[0].value =
                        Object.values(item)[0].value < 60 ? 1 : Math.round(Object.values(item)[0].value / 60)),
            );

        return groupedData.flatMap((item) => Object.values(item));
    }, [displayUnit, downtimeEvents, i18n.language]);

    const totalEventsInMinutesOrOccurrences = useMemo(() => {
        return pieChartData.reduce((acc: number, current: IDataItem) => acc + current.value, 0);
    }, [pieChartData]);

    useEffect(() => {
        if (pieChartData) {
            const nonStringValues = pieChartData.map((item) =>
                Object.values(item).filter((data) => typeof data !== "string"),
            );

            setNoInitialDataInMinutes(
                Object.values(nonStringValues).every((item) => typeof item[0] === "number" && isNaN(item[0])),
            );
        }
    }, [pieChartData]);

    return (
        <>
            {showDowntimeDetailsHeader && downtimeSelected && (
                <DowntimeDetailsHeader downtimeSelected={downtimeSelected} />
            )}
            <Box className={classes.container}>
                {showTitle && (
                    <Typography variant="h1" className={classes.overallSummaryTitle}>
                        {t("HMI.overall_summary.title")}
                    </Typography>
                )}
                {downtimeEvents && downtimeEvents!.length > 0 ? (
                    <>
                        {store.machineCenter?.production_line.production_line_shifts.length! > 0 && (
                            <HMIUptimeDowntimeBar />
                        )}
                        <Grid container className={classes.graphContainer}>
                            {noInitialDataInMinutes ? (
                                <Grid item xs={12} className={classes.graphGridItemContainer}>
                                    <GraphsNotEnoughDataInMinutes />
                                </Grid>
                            ) : (
                                <>
                                    <Grid item xs={8} className={classes.graphGridItemContainer}>
                                        <OverallDowntimeSummaryPieChart
                                            data={pieChartData}
                                            totalEventsInMinutesOrOccurrences={totalEventsInMinutesOrOccurrences!}
                                            pieChartCenteredMetric={OverallDowntimeSummaryPieChartCenteredMetric(
                                                highlight!,
                                            )}
                                            setHighlight={setHighlight}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <OverallDowntimeSummaryPieChartLegends
                                            pieChartData={pieChartData}
                                            totalEventsInMinutesOrOccurrences={totalEventsInMinutesOrOccurrences!}
                                            countryCode={countryCode}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </>
                ) : (
                    <NoStatsData />
                )}
            </Box>
        </>
    );
};
